import { Tooltip } from '@alefeducation/ui-elements-library-core';
import moment from 'moment';
import React from 'react';
import { StatusInfo } from '../pages/EsisLogs/InfoTuple';

export type EsisJobType = 'Export Product Mapping' | 'Import Product Mapping';
export type EsisJobTypeValue =
  | 'exportProductMappingCsvJob'
  | 'importProductCourseCodeMappingFromCSVJob';

export const esisJobTypeMap: Record<EsisJobType, EsisJobTypeValue> = {
  'Export Product Mapping': 'exportProductMappingCsvJob',
  'Import Product Mapping': 'importProductCourseCodeMappingFromCSVJob',
} as const;

export const JobTypes: EsisJobType[] = [
  'Export Product Mapping',
  'Import Product Mapping',
];

export function getEsisJobTypeByValue(
  value: EsisJobTypeValue,
): EsisJobType | undefined {
  const entry = Object.entries(esisJobTypeMap).find(([_, v]) => v === value);
  return entry?.[0] as EsisJobType | undefined;
}

export const getDefaultDateFormat = (date: any) =>
  moment(date).format('Do MMM, YYYY h:mm a');

export const getLastModifiedTime = (jobInfo: any) => {
  switch (jobInfo.status) {
    case 'IN QUEUE':
      return jobInfo.createdOn;
    case 'STARTED':
      return jobInfo.startTime;
    case 'PENDING':
      return jobInfo.startTime;
    case 'COMPLETED':
      return jobInfo.endTime;
    case 'FAILED':
      return jobInfo.endTime;
  }
};

const NOT_APPLICABLE = 'N/A';

export const getJobCompletedInTime = (jobInfo: any) => {
  if (
    jobInfo.startTime === NOT_APPLICABLE ||
    jobInfo.endTime === NOT_APPLICABLE
  ) {
    return '';
  }
  const startTime = moment(jobInfo.startTime);
  const endTime = moment(jobInfo.endTime);
  const duration = endTime.diff(startTime, 'millisecond');
  const momentTime = moment.utc(duration);

  const hours = momentTime.hours();
  const minutes = momentTime.minutes();
  const seconds = momentTime.seconds();

  let timeString = `${seconds}sec`;

  if (minutes) timeString = `${minutes}min ${timeString}`;

  if (hours) timeString = `${hours}hr ${timeString}`;

  return timeString;
};

export const jobDetailColumns = [
  {
    header: 'Record Id',
    accessorKey: 'correlationalId',
    size: 300,
  },
  {
    header: 'Scenario',
    size: 201,
    accessorKey: 'changeType',
  },
  {
    header: 'Entity Type',
    accessorKey: 'entityType',
  },
  {
    header: 'Error Message',
    accessorKey: 'errorMessage',
  },
];

export const columns = [
  {
    header: 'File Name',
    accessorKey: 'fileName',
    cell: (info: any) => <div>{info.getValue()}</div>,
  },
  {
    header: 'Type',
    size: 240,
    accessorKey: 'importType',
    cell: (info: any) => {
      return getEsisJobTypeByValue(info.getValue() || '');
    },
  },
  {
    header: 'Started By',
    accessorKey: 'startedBy',
  },
  {
    header: 'Last Modified',
    size: 250,
    accessorKey: 'createdOn',
    cell: (info: any) => {
      const modifiedTime = getLastModifiedTime(info.row.original);

      return (
        <span style={{ display: 'inline-block', textAlign: 'center' }}>
          {getDefaultDateFormat(modifiedTime)}
        </span>
      );
    },
  },
  {
    header: 'Status',
    size: 120,
    accessorKey: 'status',
    cell: (info: any) => {
      const jobCompletedInTime = getJobCompletedInTime(info.row.original);
      return jobCompletedInTime ? (
        <Tooltip
          content={`Took ${jobCompletedInTime}`}
          data-testid={'jobs-info-tooltip'}
          id={`${info.cell.id}-jobs-info-tooltip`}
          position={'bottom'}
        >
          <StatusInfo status={info.getValue()} />
        </Tooltip>
      ) : (
        <StatusInfo status={info.getValue()} />
      );
    },
  },
];

export interface StatusInfoPropsType {
  status: string;
}
